/**
 * KYC review — the queue, and why anything is in it.
 *
 * Every submission here reached a human because the automated vendor chain could not finish
 * the job, and the *reason* it could not is the single most useful thing a reviewer can be
 * told. "The name on the record does not match" and "no identity vendor is live" look
 * identical to the customer and want opposite responses from the operator: the first is a
 * document to compare, the second is a rail to fix and nothing in this queue will help. So
 * the reason rides on the row, in operator language, and a reviewer can triage a full queue
 * without opening a single item.
 *
 * Three things this page does beyond listing:
 *
 * - **The watchdog.** The routing rules deliberately send every inconclusive check to a
 *   human, and a queue nobody is watching is exactly how "route it to a human" decays into
 *   "leave it forever". The strip at the top is how deep the queue is and how old its oldest
 *   item is, turning amber past the SLA. The threshold and the verdict both come from the
 *   API (`staleAfterSeconds`, `stale`), so the console cannot form its own opinion about what
 *   stale means and disagree with the contract.
 * - **Re-running the automated check.** Failover in the other direction. Most of what lands
 *   here landed because a vendor was down or unconfigured, and that is usually temporary —
 *   so a reviewer can ask the machine again before a person is spent on it. On a pass the API
 *   raises the tier on the vendor's word, exactly as the customer path would, and the item
 *   leaves the queue.
 * - **Honest nulls.** `providerSlug` is null when more than one identity vendor is live,
 *   because the composed chain reports the answer and not which member gave it. That is
 *   rendered as what it is, not as "unknown" — an operator who reads "unknown vendor" goes
 *   looking for a bug that is not there.
 *
 * The raw BVN/NIN never leaves the API; only the last four are ever shown. Approve, reject
 * and re-run are all gated on `kyc:review` (compliance, superadmin); `kyc:read` sees the queue.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { SkeletonLines, SkeletonTable } from '../components/Skeleton.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  AdminKycDetail,
  AdminKycPendingResponse,
  AdminKycQueueItem,
  KycAutomatedOutcome,
  KycDocumentSummary,
  KycRecheckResponse,
} from '../contracts/types.js';
import { Badge, Button, Card, EmptyState, ErrorBox, Field, Textarea } from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { KycStatusBadge, TierBadge } from '../components/badges.js';
import { IconAlert, IconEye, IconRefresh } from '../components/icons.js';
import { ConfirmModal, Modal } from '../components/Modal.js';
import { DocumentViewer } from '../components/DocumentViewer.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatDate, formatDateTime, humanize, relativeTime } from '../lib/format.js';

/**
 * Why a submission is in front of a human, in the words an operator would use — and, next
 * to it, what they are supposed to do about it.
 *
 * Deliberately not the vendor's vocabulary. `not_found` is a lookup result; "no record
 * found" is a thing a reviewer can act on. And `unsupported` is separated from `error` here
 * exactly as the contract separates them: one is nobody configured a vendor, the other is a
 * vendor that was asked and did not answer, and only the first is fixed on a different page.
 */
const OUTCOME_COPY: Readonly<
  Record<KycAutomatedOutcome, { label: string; tone: Tone; detail: string }>
> = {
  mismatch: {
    label: 'Name did not match',
    tone: 'amber',
    detail:
      'The vendor found the record but the name on it does not match this account. Compare the document against the account details. A married name, a swapped first and last name, or a typo are all common and all legitimate.',
  },
  not_found: {
    label: 'No record found',
    tone: 'amber',
    detail:
      'The vendor has no record for that number. Usually a mistyped BVN or NIN; occasionally a number that is genuinely not registered.',
  },
  error: {
    label: 'The vendor did not answer',
    tone: 'red',
    detail:
      'A vendor was asked and the call failed or timed out. Nothing is wrong with the document, so re-run the check before spending a reviewer on it.',
  },
  unsupported: {
    label: 'No identity vendor is live',
    tone: 'red',
    detail:
      'No identity provider is enabled, so nothing was asked at all. This is a configuration gap rather than a problem with the submission: fix it on Provider keys, then re-run the check.',
  },
  verified: {
    label: 'Vendor matched, held for review',
    tone: 'violet',
    detail:
      'The automated check passed. It is still here because the tier being asked for needs a human to sign it off as well.',
  },
};

/** The badge text for one row, with the tier_3 case called out rather than folded in. */
function triageOf(item: Pick<AdminKycQueueItem, 'providerOutcome' | 'targetTier'>): {
  label: string;
  tone: Tone;
  detail: string;
} {
  if (item.providerOutcome === null) {
    return {
      label: 'Not checked automatically',
      tone: 'neutral',
      detail:
        'No automated result is recorded against this submission. It predates the vendor chain, or was filed before any check ran. Re-run the check to get one.',
    };
  }
  const copy = OUTCOME_COPY[item.providerOutcome];
  // A tier_3 upgrade goes to a human even on a clean vendor pass, and telling a reviewer
  // "vendor matched" without saying why it is nevertheless in their queue is the fastest
  // way to have it approved without the second look the tier exists to require.
  if (item.providerOutcome === 'verified' && item.targetTier === 'tier_3') {
    return {
      label: 'Needs a second look for tier 3',
      tone: 'violet',
      detail:
        'The automated identity check passed. Tier 3 asks for more than an identity match. It wants a government photo ID and a checked home address, so it is here for that judgement, not for the BVN or NIN.',
    };
  }
  return copy;
}

export function KycReviewPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canReview = session !== null && adminCan(session.me.role, 'kyc:review');
  const state = useAsync(() => api.kycQueue(), []);
  const health = useAsync(() => api.kycPending(), []);

  const [rejecting, setRejecting] = useState<AdminKycQueueItem | null>(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  /** Drop a decided item and refresh the watchdog, which the decision just changed. */
  function drop(id: string) {
    state.setData((prev) =>
      prev === null ? prev : { ...prev, items: prev.items.filter((i) => i.id !== id) },
    );
    health.reload();
    setOpenId((current) => (current === id ? null : current));
  }

  async function approve(item: AdminKycQueueItem) {
    setBusyId(item.id);
    try {
      await api.reviewKyc(item.id, { decision: 'approve' });
      drop(item.id);
      toast.success('KYC approved', `${item.displayName ?? 'Customer'} raised to ${item.targetTier.replace('tier_', 'Tier ')}.`);
    } catch (err) {
      toast.error('Could not approve', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  async function reject() {
    if (rejecting === null) return;
    setBusyId(rejecting.id);
    try {
      await api.reviewKyc(rejecting.id, {
        decision: 'reject',
        reason: reason.trim() || 'Document could not be verified.',
      });
      drop(rejecting.id);
      toast.success('KYC rejected');
      setRejecting(null);
      setReason('');
    } catch (err) {
      toast.error('Could not reject', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  const items = state.data?.items ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">ID checks</h1>
        <p className="page-head__sub">
          Identity submissions the automated check could not finish. Each row says why it is here,
          so a full queue can be triaged without opening every item. Only the document’s last four
          digits are shown. The full BVN/NIN stays encrypted behind the API.
          {!canReview && ' Your role can view the queue but not decide on it.'}
        </p>
      </div>

      <QueueWatchdog state={health.data} loading={health.loading} />

      <Card
        flush
        title="Pending queue"
        actions={<Badge tone={items.length > 0 ? 'amber' : 'green'}>{items.length} pending</Badge>}
      >
        {state.loading ? (
          <SkeletonTable columns={5} rows={6} label="Loading the queue" />
        ) : state.error !== null ? (
          <ErrorBox message={state.error} />
        ) : items.length === 0 ? (
          <EmptyState glyph="✓" title="Queue is clear" hint="No identity submissions are waiting." />
        ) : (
          <div className="table-wrap">
            <table className="table table--rows-clickable">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Why it is here</th>
                  <th>Target</th>
                  <th>Document</th>
                  <th>Date of birth</th>
                  <th>Submitted</th>
                  <th style={{ textAlign: 'right' }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const triage = triageOf(item);
                  return (
                    <tr key={item.id} onClick={() => setOpenId(item.id)}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{item.displayName ?? '–'}</div>
                        <div className="mono faint" style={{ fontSize: 11.5, marginTop: 3 }}>
                          {item.maskedPhone}
                        </div>
                      </td>
                      <td>
                        <Badge tone={triage.tone} dot>
                          {triage.label}
                        </Badge>
                      </td>
                      <td>
                        <TierBadge tier={item.targetTier} />
                      </td>
                      <td>
                        <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>{item.documentKind}</span>{' '}
                        <span className="mono faint">•••• {item.documentLast4}</span>
                      </td>
                      <td className="muted">{formatDate(item.dateOfBirth)}</td>
                      <td className="muted" title={item.submittedAt}>{relativeTime(item.submittedAt)}</td>
                      {/* The decision buttons must not also open the detail, so the row's
                          click is stopped here rather than on each button. */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Button
                            size="sm"
                            variant="accent"
                            disabled={!canReview || busyId === item.id}
                            onClick={() => void approve(item)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={!canReview || busyId === item.id}
                            onClick={() => {
                              setRejecting(item);
                              setReason('');
                            }}
                          >
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openId !== null && (
        <KycDetailModal
          id={openId}
          canReview={canReview}
          onClose={() => setOpenId(null)}
          onResolved={drop}
          onStillPending={() => {
            // A re-run that could not decide leaves the item in place with fresher
            // provenance on it, so the row's badge has to be reloaded to match.
            state.reload();
            health.reload();
          }}
        />
      )}

      <ConfirmModal
        open={rejecting !== null}
        title="Reject this submission?"
        description="The customer stays at their current tier and sees the reason you give below."
        confirmLabel="Reject submission"
        danger
        loading={busyId !== null}
        onConfirm={reject}
        onClose={() => setRejecting(null)}
      >
        <Field label="Reason (shown to the customer)">
          <Textarea
            value={reason}
            maxLength={200}
            placeholder="e.g. Name on document does not match account"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </ConfirmModal>
    </>
  );
}

/**
 * The queue's health, in one line: how many are waiting, and how long the oldest has been.
 *
 * Age of the oldest item is the number that catches a queue going unwatched — a depth of
 * three is fine on a Tuesday and an emergency if the oldest of them is a week old — which is
 * why it is here rather than only a count. `stale` comes from the API precomputed, so this
 * renders the API's verdict rather than re-deriving one that could disagree.
 */
function QueueWatchdog({
  state,
  loading,
}: {
  state: AdminKycPendingResponse | null;
  loading: boolean;
}): JSX.Element | null {
  if (loading || state === null) return null;

  const hours = Math.round(state.staleAfterSeconds / 3600);
  const clear = state.pending === 0;

  return (
    <div className={`kyc-watch${state.stale ? ' kyc-watch--stale' : clear ? ' kyc-watch--clear' : ''}`} role="status">
      {state.stale && <IconAlert size={18} />}
      <div>
        <strong>
          {clear
            ? 'Nobody is waiting.'
            : `${state.pending} ${state.pending === 1 ? 'submission is' : 'submissions are'} waiting for a human.`}
        </strong>{' '}
        {clear ? (
          <>Every submission the automated chain could not decide has been dealt with.</>
        ) : state.stale ? (
          <>
            The oldest has been waiting {relativeTime(state.oldestSubmittedAt).replace(' ago', '')}, past
            the {hours}-hour service level. A customer at the front of this queue has money they
            cannot move until someone looks.
          </>
        ) : (
          <>
            The oldest arrived {relativeTime(state.oldestSubmittedAt)}, inside the {hours}-hour service
            level.
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One submission in full: what the vendor said, which vendor said it, and the two ways
 * forward — ask the machine again, or decide it here.
 *
 * The re-run is the manual-to-automatic direction of the failover and is the whole reason the
 * endpoint exists. Its result is shown rather than swallowed, because "I pressed it and
 * nothing visible happened" is how an operator concludes a button is broken and stops using
 * the cheapest tool they have.
 */
function KycDetailModal({
  id,
  canReview,
  onClose,
  onResolved,
  onStillPending,
}: {
  id: string;
  canReview: boolean;
  onClose: () => void;
  onResolved: (id: string) => void;
  onStillPending: () => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.kycItem(id), [id]);
  /*
   * Metadata only, on its own read.
   *
   * Its own request rather than a field on the submission, because the two answer different
   * questions and fail independently: a reviewer whose *document list* could not load still
   * needs the vendor provenance in front of them, and a submission that loaded fine should not
   * be hidden because a second call did not.
   *
   * Never the bytes. Those come one at a time from `getKycDocument`, and every one of those
   * fetches writes an audit row naming the single document that was opened — which is the
   * whole argument for it being its own route rather than a field on this read.
   */
  const documents = useAsync(() => api.kycDocuments(id), [id]);
  const [rechecking, setRechecking] = useState(false);
  const [result, setResult] = useState<KycRecheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The one document currently open, or null. See `DocumentViewer`. */
  const [viewing, setViewing] = useState<KycDocumentSummary | null>(null);

  const detail: AdminKycDetail | null = state.data;
  const docs = documents.data?.documents ?? [];

  async function recheck(): Promise<void> {
    setRechecking(true);
    setError(null);
    try {
      const answer = await api.recheckKyc(id);
      setResult(answer);
      if (answer.status === 'verified') {
        toast.success(
          'The automated check passed',
          'The tier has been raised on the vendor’s answer, and this submission has left the queue.',
        );
        onResolved(id);
        return;
      }
      // Still undecided — the item stays put, with fresher provenance. Reload rather than
      // patch, so what is on screen is what the API now holds.
      state.reload();
      onStillPending();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setRechecking(false);
    }
  }

  const triage = detail === null ? null : triageOf(detail);

  return (
    <Modal
      open
      title="Identity submission"
      /*
        This used to end "the raw document is never served here, only its last four digits",
        which was true when it was written and is the opposite of the point now. The document
        IS served here — that is what a manual review is for — behind the operator's own token,
        one at a time, with an audit row per fetch. What is still never served is the raw
        document *number*: `documentNumber` is published masked, deliberately, because a BVN is
        a lifetime identifier that cannot be rotated after a leak.
      */
      description="What the automated chain found, the vendor that found it, and the documents the customer filed. Opening one is recorded against your name."
      onClose={onClose}
      footer={
        <>
          <Button
            variant="ghost"
            loading={rechecking}
            disabled={!canReview || detail === null || detail.status !== 'pending'}
            onClick={() => void recheck()}
            title="Ask the identity vendors again. On a pass the tier is raised automatically."
          >
            <IconRefresh size={14} />
            Re-run the automated check
          </Button>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {state.loading ? (
        <SkeletonLines lines={6} label="Loading the submission" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : detail === null || triage === null ? (
        <ErrorBox message="This submission is no longer available." />
      ) : (
        <>
          <div className="kv">
            <dt>Customer</dt>
            <dd style={{ fontWeight: 600 }}>{detail.displayName ?? '–'}</dd>
            <dt>Phone</dt>
            <dd className="mono">{detail.maskedPhone}</dd>
            <dt>Asking for</dt>
            <dd><TierBadge tier={detail.targetTier} /></dd>
            <dt>Document</dt>
            <dd>
              <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>{detail.documentKind}</span>{' '}
              {/*
                The number as the contract publishes it, which is masked and will stay masked.
                It is not annotated as incomplete, because it is not: the reviewer's question is
                whether the document in front of them matches the record, and that is answered
                by the scan below against the length and the last four. A raw BVN in a browser
                is a lifetime identifier in an HTTP cache and in every screenshot anybody takes.
              */}
              <span className="mono faint">{detail.documentNumber ?? `•••• ${detail.documentLast4}`}</span>
            </dd>
            <dt>Date of birth</dt>
            <dd>{formatDate(detail.dateOfBirth)}</dd>
            <dt>Submitted</dt>
            <dd title={detail.submittedAt}>{relativeTime(detail.submittedAt)}</dd>
            <dt>Status</dt>
            <dd><KycStatusBadge status={detail.status} /></dd>
          </div>

          {/*
            The scans, which are the whole reason this queue has a human in it.

            Metadata is listed and the bytes are fetched one at a time, on a tap. That split is
            deliberate: a list that pre-loaded every image would write an audit row for a
            document nobody looked at, which makes the audit trail useless in exactly the way
            that matters — it would no longer distinguish "opened" from "was on a page".
          */}
          <div style={{ marginTop: 'var(--space-5)' }}>
            <div className="states__label">Documents filed</div>
            {documents.loading ? (
              <p className="muted" style={{ marginTop: 8 }}>
                Loading the document list…
              </p>
            ) : documents.error !== null ? (
              /*
                Said as a failed read, not as an empty list. "We could not ask" and "the
                customer filed nothing" lead a reviewer to opposite decisions, and collapsing
                them would have somebody reject a submission for missing a document that is
                sitting on the server.
              */
              <p className="muted" style={{ marginTop: 8 }}>
                We could not load the document list. Nothing is wrong with the submission.{' '}
                <Button size="sm" variant="ghost" onClick={() => documents.reload()}>
                  Try again
                </Button>
              </p>
            ) : docs.length === 0 ? (
              <p className="muted" style={{ marginTop: 8 }}>
                No documents were filed with this submission.
              </p>
            ) : (
              <>
                <div className="docs">
                  {docs.map((doc) => (
                    <button
                      type="button"
                      className="docs__item"
                      key={doc.id}
                      onClick={() => setViewing(doc)}
                    >
                      <IconEye size={15} />
                      <span className="docs__name">{humanize(doc.kind)}</span>
                      <span className="docs__meta faint mono">
                        {doc.mime === 'application/pdf' ? 'PDF' : 'Image'}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="docs__note">
                  Opening a document is recorded against your name, with the document it was.
                </p>
              </>
            )}
          </div>

          <div className="kyc-why">
            <div className="kyc-why__head">
              <Badge tone={triage.tone} dot>
                {triage.label}
              </Badge>
              {detail.autoCheckedAt !== null && (
                <span className="faint" style={{ fontSize: 12 }}>
                  checked {relativeTime(detail.autoCheckedAt)}
                </span>
              )}
            </div>
            <p className="kyc-why__body">{triage.detail}</p>

            <div className="kv" style={{ marginTop: 4 }}>
              <dt>Identity vendor</dt>
              <dd>
                {detail.providerSlug !== null ? (
                  <span className="mono">{detail.providerSlug}</span>
                ) : detail.providerOutcome === null || detail.providerOutcome === 'unsupported' ? (
                  // Two genuinely different nulls, and calling either one "unknown" would
                  // send an operator hunting for a bug. Nothing was asked here.
                  <span className="faint">No vendor was called.</span>
                ) : (
                  <span className="faint">
                    Not attributable. More than one vendor is live, and the composed chain reports
                    the answer rather than which member gave it.
                  </span>
                )}
              </dd>
              <dt>Vendor reference</dt>
              <dd className="mono">
                {detail.providerReference ?? <span className="faint">none</span>}
              </dd>
              <dt>Decided by</dt>
              <dd>
                {detail.decidedBy === null ? (
                  <span className="faint">Nobody yet. This is what you are deciding.</span>
                ) : detail.decidedBy === 'auto' ? (
                  'The vendor chain, automatically'
                ) : (
                  'A reviewer in this console'
                )}
              </dd>
              {detail.reviewedAt !== null && (
                <>
                  <dt>Reviewed</dt>
                  <dd>{formatDateTime(detail.reviewedAt)}</dd>
                </>
              )}
            </div>
          </div>

          {result !== null && (
            <div
              className={`kyc-recheck kyc-recheck--${result.status === 'verified' ? 'pass' : 'hold'}`}
              role="status"
            >
              <strong>
                {result.status === 'verified'
                  ? 'The re-run passed.'
                  : 'The re-run still could not decide it.'}
              </strong>{' '}
              {result.status === 'verified' ? (
                <>
                  The tier has been raised on the vendor’s answer and this submission has left the
                  queue, so no manual decision is needed.
                </>
              ) : (
                <>
                  {OUTCOME_COPY[result.outcome].label.toLowerCase()}
                  {result.reason === null ? '.' : `: ${result.reason}`} It stays in the queue for you.
                </>
              )}{' '}
              <span className="faint">
                {result.providerSlug === null ? 'Chain' : result.providerSlug}
                {result.providerReference === null ? '' : ` · ${result.providerReference}`} ·{' '}
                {formatDateTime(result.checkedAt)}
              </span>
            </div>
          )}

          {error !== null && <ErrorBox message={error} />}

          {detail.reason !== null && detail.status === 'rejected' && (
            <div className="error-box" style={{ marginTop: 12 }}>
              <span>Rejected: {detail.reason}</span>
            </div>
          )}
        </>
      )}
      {/*
        The viewer is a dialog of its own on top of this one, rather than replacing the panel.

        A reviewer comparing a scan against the record needs the record to still exist behind
        it: swapping the content of this modal would mean reading the name, opening the scan,
        and then having to close it to check the name again.
      */}
      {viewing !== null && (
        <DocumentViewer document={viewing} onClose={() => setViewing(null)} />
      )}
    </Modal>
  );
}
